import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Modal,
  Typography,
  TextField,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { validatePasswordStrength, PASSWORD_REQUIREMENTS_MESSAGE } from "../../utils/password";
import {
  normalizeEmailAddress,
  normalizePhoneNumber,
  sanitizePhoneNumberInput,
  validateOptionalEmailAddress,
  validatePhoneNumber,
} from "../../utils/contactValidation";
import {
  subscribeClient,
  getMySubscription,
  getPlans,
  requestSubscriptionCancellation,
  getMyProfile,
} from "../../services/clientService";
import { createRelative, getRelativesList, updateRelative } from "../../services/relativeService";

const tabLabels = [
  "Account Overview",
  "Change Email",
  "Change Password",
  "Emergency Contacts",
  "Manage Subscription",
  "Notifications",
  "Delete Account",
];

const splitRelativeName = (relativeName = "") => {
  const trimmed = relativeName.trim();
  if (!trimmed) {
    return { firstName: "", lastName: "" };
  }
  const [firstName, ...rest] = trimmed.split(/\s+/);
  return {
    firstName,
    lastName: rest.join(" "),
  };
};

const mapRelativeToContact = (relative) => {
  const { firstName, lastName } = splitRelativeName(relative.relative_name);
  return {
    id: relative.relative_id ?? relative.id,
    firstName,
    lastName,
    phone: relative.tel_no || "",
    email: relative.email || "",
    relation: relative.relation || "Relative",
    client: relative.client,
    isNew: false,
  };
};

const buildRelativePayload = (contact, clientId) => ({
  relative_name: [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim(),
  tel_no: normalizePhoneNumber(contact.phone || ""),
  email: normalizeEmailAddress(contact.email || ""),
  relation: contact.relation || "Relative",
  client: clientId,
});

const getContactValidationErrors = (contact) => ({
  phone: validatePhoneNumber(contact?.phone || ""),
  email: validateOptionalEmailAddress(contact?.email || ""),
});

const ClientProfile = () => {
  const { user, requestEmailChange, changePassword, logout } = useAuth();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [plans, setPlans] = useState([]);
  const [subscribingPlanId, setSubscribingPlanId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [editingContactId, setEditingContactId] = useState(null);
  const [pendingContact, setPendingContact] = useState(null);
  const [emailForm, setEmailForm] = useState({ currentPassword: "", newEmail: "" });
  const [emailError, setEmailError] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const relationOptions = ["Relative", "Partner", "Carer", "Guardian", "Other"];
  const navigate = useNavigate();

  const fetchSubscription = async () => {
    try {
      const data = await getMySubscription();
      const source = data?.subscription ?? (data?.plan ? data : null);
      const next =
        source && source.plan
          ? {
              plan: source.plan,
              status: source.status,
              start_date: source.start_date,
              expiry_date: source.expiry_date,
              cancellation_pending: !!source.cancellation_pending,
            }
          : null;
      setSubscription(next);
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const data = await getMyProfile();
        setProfile(data);
      } catch (err) {
        console.error("Failed to load profile:", err);
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    const loadEmergencyContacts = async () => {
      setContactsLoading(true);
      try {
        const data = await getRelativesList();
        const contacts = Array.isArray(data) ? data.map(mapRelativeToContact) : [];
        setEmergencyContacts(contacts);
      } catch (err) {
        console.error("Failed to load emergency contacts:", err);
        setSnackbar({ open: true, message: "Failed to load emergency contacts." });
      } finally {
        setContactsLoading(false);
      }
    };

    if (user?.client_id) {
      loadEmergencyContacts();
    }
  }, [user?.client_id]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await getPlans();
        const planList = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
          ? data.results
          : [];
        setPlans(planList);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        setSnackbar({ open: true, message: "Failed to load available plans." });
      }
    };
    fetchPlans();
  }, []);

  const handleSubscribe = async (planId) => {
    setSubscribingPlanId(planId);
    try {
      const data = await subscribeClient(planId);
      setSubscription((prev) => ({
        plan: data.plan,
        status: data.status || "active",
        start_date: data.start_date || prev?.start_date || null,
        expiry_date: data.expiry_date,
        cancellation_pending: false,
      }));
      setSnackbar({ open: true, message: data.message || "Subscribed successfully!" });
    } catch (err) {
      console.error("Subscription failed:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || err?.message || "Subscription failed",
      });
    } finally {
      setSubscribingPlanId(null);
    }
  };

  const handleCancelRequest = async () => {
    try {
      await requestSubscriptionCancellation();
      setSubscription((prev) =>
        prev
          ? {
              ...prev,
              cancellation_pending: true,
            }
          : prev
      );
      setSnackbar({ open: true, message: "Cancellation request submitted." });
      fetchSubscription();
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || "Unable to submit cancellation request.";
      setSnackbar({ open: true, message });
    }
  };

  const handleEditContact = (contact) => {
    setEditingContactId(contact.id);
    setPendingContact({ ...contact });
  };

  const handleContactChange = (field, value) => {
    let nextValue = value;
    if (field === "phone") {
      nextValue = sanitizePhoneNumberInput(value);
    }
    if (field === "email") {
      nextValue = value.trimStart();
    }

    setPendingContact((prev) => ({ ...prev, [field]: nextValue }));
  };

  const handleSaveContact = async () => {
    if (!pendingContact) return;
    const fullName = [pendingContact.firstName, pendingContact.lastName].filter(Boolean).join(" ").trim();
    if (!fullName || !pendingContact.phone) {
      setSnackbar({ open: true, message: "Name and phone number are required." });
      return;
    }

    const validationErrors = getContactValidationErrors(pendingContact);
    if (validationErrors.phone || validationErrors.email) {
      setSnackbar({
        open: true,
        message: validationErrors.phone || validationErrors.email,
      });
      return;
    }

    try {
      const payload = buildRelativePayload(
        {
          ...pendingContact,
          phone: normalizePhoneNumber(pendingContact.phone),
          email: normalizeEmailAddress(pendingContact.email),
        },
        user?.client_id
      );
      const savedContact = pendingContact.isNew
        ? await createRelative(payload)
        : await updateRelative(editingContactId, payload);
      const mappedContact = mapRelativeToContact(savedContact);

      setEmergencyContacts((prev) => {
        if (pendingContact.isNew) {
          return prev.map((contact) => (contact.id === editingContactId ? mappedContact : contact));
        }
        return prev.map((contact) => (contact.id === editingContactId ? mappedContact : contact));
      });
      setEditingContactId(null);
      setPendingContact(null);
      setSnackbar({
        open: true,
        message: pendingContact.isNew ? "Emergency contact added." : "Emergency contact updated.",
      });
    } catch (err) {
      console.error("Failed to save emergency contact:", err);
      setSnackbar({
        open: true,
        message: err?.message || "Failed to save emergency contact.",
      });
    }
  };

  const handleCancelContactEdit = () => {
    if (pendingContact?.isNew) {
      setEmergencyContacts((prev) => prev.filter((contact) => contact.id !== editingContactId));
    }
    setEditingContactId(null);
    setPendingContact(null);
  };

  const handleEmailFieldChange = (field, value) => {
    setEmailForm((prev) => ({ ...prev, [field]: value }));
    if (field === "newEmail") {
      if (!value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setEmailError("");
      } else {
        setEmailError("Please enter a valid email address.");
      }
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    const { currentPassword, newEmail } = emailForm;

    if (!currentPassword || !newEmail) {
      setEmailError("Current password and new email are required.");
      setEmailMessage("");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailError("Please enter a valid email address.");
      setEmailMessage("");
      return;
    }

    setEmailLoading(true);
    setEmailError("");
    setEmailMessage("");
    const result = await requestEmailChange({ currentPassword, newEmail });
    if (result?.success) {
      setEmailMessage(result.message || "We sent a confirmation link to your new email address.");
      setEmailForm({ currentPassword: "", newEmail: "" });
    } else {
      setEmailError(result?.message || "Unable to request email change.");
    }
    setEmailLoading(false);
  };

  const handlePasswordFieldChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    if (field === "newPassword") {
      if (!value) {
        setPasswordError("");
        return;
      }
      const validation = validatePasswordStrength(value);
      setPasswordError(validation.valid ? "" : validation.message);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setSnackbar({ open: true, message: "All password fields are required." });
      return;
    }

    const validation = validatePasswordStrength(newPassword);
    if (!validation.valid) {
      setPasswordError(validation.message);
      setSnackbar({ open: true, message: validation.message });
      return;
    }

    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: "New password and confirmation do not match." });
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await changePassword({ currentPassword, newPassword, confirmPassword });
      if (!result?.success) {
        setSnackbar({ open: true, message: result?.message || "Unable to change password." });
        return;
      }

      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordError("");
      setSnackbar({ open: true, message: result.message || "Password changed successfully." });

      if (result.forceLogout) {
        setTimeout(() => {
          logout();
          navigate("/login");
        }, 600);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container sx={{ mt: 5, pb: 6 }}>
      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ textAlign: "center" }}>
          <Avatar
            src={user.profile?.image || "/default-profile.png"}
            sx={{ width: 120, height: 120, mx: "auto", mb: 2 }}
          />
          <Typography variant="h6">{user.username}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </CardContent>
        <Divider />
        <Box textAlign="center" py={2}>
          <Button variant="outlined" href="/client-dashboard">
            Back to Dashboard
          </Button>
        </Box>
      </Card>

      <Box mt={4}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabLabels.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      <Box mt={3}>
        <TabPanel value={activeTab} index={0}>
          <Card>
            <CardContent>
              {[
                { label: "Username", value: profile?.username || user.username },
                { label: "Email", value: profile?.email || user.email },
                {
                  label: "Joined",
                  value:
                    (profile?.date_joined
                      ? new Date(profile.date_joined)
                      : user.date_joined
                      ? new Date(user.date_joined)
                      : null)?.toLocaleDateString() || "-",
                },
                { label: "Phone", value: profile?.phone || "-" },
                { label: "Address", value: profile?.address || "-" },
              ].map((item, idx, arr) => (
                <Box
                  key={item.label}
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={1}
                  py={1.5}
                  borderBottom={idx < arr.length - 1 ? "1px solid #e5e7eb" : "none"}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography fontWeight={600}>{item.value}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Change Email
          </Typography>
          <Box component="form" onSubmit={handleEmailSubmit} sx={{ maxWidth: 520 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              For security, we will send a confirmation link to your new email address before updating your account.
            </Typography>
            {emailMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {emailMessage}
              </Alert>
            )}
            {emailError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {emailError}
              </Alert>
            )}
            <TextField
              label="Current Password"
              type="password"
              value={emailForm.currentPassword}
              onChange={(event) => handleEmailFieldChange("currentPassword", event.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="New Email"
              type="email"
              value={emailForm.newEmail}
              onChange={(event) => handleEmailFieldChange("newEmail", event.target.value)}
              fullWidth
              margin="normal"
              required
              error={Boolean(emailError) && emailError.toLowerCase().includes("email")}
            />
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={emailLoading}>
              {emailLoading ? "Sending..." : "Send Confirmation Link"}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Change Password
          </Typography>
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ maxWidth: 520 }}>
            <TextField
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={(event) => handlePasswordFieldChange("currentPassword", event.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={(event) => handlePasswordFieldChange("newPassword", event.target.value)}
              fullWidth
              margin="normal"
              required
              error={Boolean(passwordError)}
              helperText={passwordError || PASSWORD_REQUIREMENTS_MESSAGE}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(event) => handlePasswordFieldChange("confirmPassword", event.target.value)}
              fullWidth
              margin="normal"
              required
            />
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={passwordLoading}>
              {passwordLoading ? "Updating..." : "Change Password"}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Emergency Contacts
          </Typography>
          {contactsLoading ? (
            <Typography color="text.secondary">Loading emergency contacts...</Typography>
          ) : (
          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>First name</TableCell>
                  <TableCell>Surname</TableCell>
                  <TableCell>Phone Number</TableCell>
                  <TableCell>Email Address</TableCell>
                  <TableCell>Relation</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emergencyContacts.map((contact) => {
                  const isEditing = editingContactId === contact.id;
                  const rowData = isEditing && pendingContact ? pendingContact : contact;
                  const validationErrors = getContactValidationErrors(rowData);
                  return (
                    <TableRow key={contact.id} hover>
                      <TableCell sx={{ minWidth: 160 }}>
                        {isEditing ? (
                          <TextField
                            value={rowData.firstName}
                            onChange={(e) => handleContactChange("firstName", e.target.value)}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          rowData.firstName
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {isEditing ? (
                          <TextField
                            value={rowData.lastName}
                            onChange={(e) => handleContactChange("lastName", e.target.value)}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          rowData.lastName
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        {isEditing ? (
                          <TextField
                            value={rowData.phone}
                            onChange={(e) => handleContactChange("phone", e.target.value)}
                            size="small"
                            fullWidth
                            type="tel"
                            inputProps={{ inputMode: "tel", maxLength: 20 }}
                            error={Boolean(rowData.phone) && Boolean(validationErrors.phone)}
                            helperText={
                              rowData.phone && validationErrors.phone
                                ? validationErrors.phone
                                : "Use 7 to 15 digits. + is allowed."
                            }
                          />
                        ) : (
                          rowData.phone
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        {isEditing ? (
                          <TextField
                            type="email"
                            value={rowData.email || ""}
                            onChange={(e) => handleContactChange("email", e.target.value)}
                            size="small"
                            fullWidth
                            error={Boolean(rowData.email) && Boolean(validationErrors.email)}
                            helperText={
                              rowData.email && validationErrors.email
                                ? validationErrors.email
                                : "Optional"
                            }
                          />
                        ) : (
                          rowData.email || "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        {isEditing ? (
                          <TextField
                            select
                            value={rowData.relation}
                            onChange={(e) => handleContactChange("relation", e.target.value)}
                            size="small"
                            fullWidth
                          >
                            {relationOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          rowData.relation
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Button size="small" variant="contained" onClick={handleSaveContact}>
                              Save
                            </Button>
                            <Button size="small" onClick={handleCancelContactEdit}>
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditContact(contact)}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {emergencyContacts.length < 5 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Box
                        display="flex"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={1}
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Add New Emergency Contact (Up to 5)
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            const newContact = {
                              id: Date.now(),
                              firstName: "",
                              lastName: "",
                              phone: "",
                              email: "",
                              relation: relationOptions[0],
                              client: user?.client_id,
                              isNew: true,
                            };
                            setEmergencyContacts((prev) => [...prev, newContact]);
                            setEditingContactId(newContact.id);
                            setPendingContact(newContact);
                          }}
                        >
                          Add Contact
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Manage Subscription
          </Typography>
          {subscription ? (
            <>
              <Typography color="text.secondary">
                You are subscribed to <strong>{subscription.plan}</strong> until{" "}
                {new Date(subscription.expiry_date).toLocaleDateString()}.
              </Typography>
              <Box mt={2}>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  disabled={subscription.cancellation_pending}
                  onClick={handleCancelRequest}
                  sx={
                    subscription.cancellation_pending
                      ? {
                          bgcolor: "#000000",
                          color: "#ffffff",
                          "&.Mui-disabled": {
                            bgcolor: "#000000",
                            color: "#ffffff",
                            opacity: 1,
                          },
                        }
                      : undefined
                  }
                >
                  {subscription.cancellation_pending
                    ? "Subscription Cancellation Request Pending"
                    : "Cancel Subscription"}
                </Button>
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">You have no active subscriptions.</Typography>
          )}

          {Array.isArray(plans) && plans.length > 0 ? (
            <Grid container spacing={2} mt={1}>
              {plans.map((plan) => (
                <Grid item xs={12} md={6} key={plan.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{plan.name} Plan</Typography>
                      <Typography color="text.secondary" gutterBottom>
                        {plan.description}
                      </Typography>
                      <Typography fontWeight="bold">
                        £{parseFloat(plan.price).toFixed(2)} / month
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          mt: 1,
                          ...(subscription?.plan === plan.name && {
                            bgcolor: "grey.500",
                            color: "common.white",
                            "&:hover": { bgcolor: "grey.600" },
                            "&.Mui-disabled": {
                              bgcolor: "grey.500",
                              color: "common.white",
                              opacity: 1,
                            },
                          }),
                        }}
                        disabled={subscription?.plan === plan.name || subscribingPlanId === plan.id}
                        onClick={() => handleSubscribe(plan.id)}
                      >
                        {subscribingPlanId === plan.id
                          ? "Updating..."
                          : subscription?.plan === plan.name
                          ? "Selected"
                          : "Subscribe"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary" mt={2}>
              {plans === undefined ? "Loading available plans..." : "No subscription plans available."}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Typography variant="h6" gutterBottom>
            Notification Preferences
          </Typography>
          <Box>
            <label>
              <input type="checkbox" /> Receive email notifications
            </label>
            <br />
            <label>
              <input type="checkbox" /> Receive SMS alerts
            </label>
          </Box>
          <Button variant="contained" size="small" sx={{ mt: 2 }}>
            Save Preferences
          </Button>
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <Typography variant="h6" color="error" gutterBottom>
            Delete Account
          </Typography>
          <Typography>Once you delete your account, there is no going back.</Typography>
          <Button
            variant="contained"
            color="error"
            sx={{ mt: 2 }}
            onClick={() => setDeleteModalOpen(true)}
          >
            Permanently Delete My Account
          </Button>
        </TabPanel>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="delete-modal-title"
      >
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 3,
            borderRadius: 2,
            width: 400,
            mx: 'auto',
            mt: '20vh',
            boxShadow: 24,
          }}
        >
          <Typography id="delete-modal-title" variant="h6" color="error" gutterBottom>
            Confirm Account Deletion
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            This action is permanent and cannot be undone. Are you sure you want to continue?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error">
              Yes, delete my account
            </Button>
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

function TabPanel({ children, value, index }) {
  if (value !== index) {
    return null;
  }
  return (
    <Box role="tabpanel" aria-hidden={value !== index}>
      {children}
    </Box>
  );
}

export default ClientProfile;
